import { IonInfiniteScroll } from '@ionic/react'
import classNames from 'classnames'
import { AnimatePresence, motion } from 'framer-motion'
import { useMemo, useState } from 'react'

import { useManagementGetAccountNosInfinite, useManagementReleaseReservedAccountNo } from '@apps-common/apis/management/account-no'
import { useManagementGetCompanies } from '@apps-common/apis/management/company'
import { AccountNoStatus } from '@apps-common/components/account-no'
import { ButtonCopy, ButtonRoundSm } from '@apps-common/components/elements/button'
import { CheckboxInputSM, ControlledInputDebounced, SelectInputSM } from '@apps-common/components/elements/form'
import {
  MODAL_WRAPPER_X_PADDING,
  ModalBodyInfiniteScrollable,
  ModalCloseButton,
  ModalContainer,
  ModalHeader,
  ModalHeaderContainer
} from '@apps-common/components/modals/modal'
import { AccountNoStatusEnum } from '@apps-common/config/account-no'
import { CompanyType, CompanyTypeId } from '@apps-common/config/company'
import { commonIcons } from '@apps-common/config/icons'
import { INPUT_CLASSES, INPUT_CONTAINER_CLASSES } from '@apps-common/config/input-styles'
import { ModalDismiss } from '@apps-common/config/modal'
import { motionFadeIn } from '@apps-common/config/motion-variants'
import { sortNumerically } from '@apps-common/helpers/array-sort'
import { IAccountNoModel } from '@apps-common/types/management/account-no'

import { InfiniteLoader, PullRefresh } from '../common'

import { ICompanyListModel } from '@apps-common/types/management/company'
import { ConfirmModal, ConfirmModalAction } from './ConfirmModal'
import { ModalButton } from './ModalButton'

export const AccountNosModal = ({
  onDismiss,
  componentProps
}: {
  onDismiss: (data?: string | null | undefined | number, role?: string) => void
  componentProps: any
}) => {
  const [mCentreId, setMCentreId] = useState<string>('')
  const [accountNo, setAccountNo] = useState<string>('')
  const [onlyAvailable, setOnlyAvailable] = useState<boolean>(false)
  const [selected, setSelected] = useState<IAccountNoModel[]>([])

  const {
    data: monitoringCentres,
    isLoading: monitoringCentresIsLoading,
    isFetching: monitoringCentresIsFetching,
    isRefetching: monitoringCentresIsRefetching,
    isError: monitoringCentresIsError
  } = useManagementGetCompanies({
    name: '',
    type: CompanyType.MonitoringCentre
  })

  const mCentresLoading =
    monitoringCentresIsLoading || monitoringCentresIsFetching || monitoringCentresIsRefetching || monitoringCentresIsError

  const {
    isLoading: accountNosIsLoading,
    isRefetching: accountNosIsReFetching,
    isFetching: accountNosIsFetching,
    isError: accountNosIsError,
    data: accountNos,
    refetch: accountNosRefetch,
    fetchNextPage: accountNosFetchNext
  } = useManagementGetAccountNosInfinite({
    enabled: !!mCentreId && mCentreId.length > 0 && !mCentresLoading,
    id: mCentreId || '',
    accountNo: accountNo || '',
    onlyAvailable,
    take: 15
  })

  const { mutate: releaseReserved, isPending: releaseReservedIsPending } = useManagementReleaseReservedAccountNo(true, () =>
    accountNosRefetch()
  )

  const copyValue = useMemo(() => {
    if (selected?.length > 0) {
      return selected
        .sort((a, b) => sortNumerically(a, b, 'id'))
        .map((s) => s.accountNo)
        .join(', ')
    }

    return null
  }, [selected, accountNos])

  const isFiltered = useMemo(
    () => (mCentreId && mCentreId?.length > 0) || accountNo?.length > 0 || onlyAvailable,
    [mCentreId, accountNo, onlyAvailable]
  )

  const loading = accountNosIsLoading || accountNosIsReFetching || accountNosIsError
  const allLoading = loading || mCentresLoading
  const updating = releaseReservedIsPending
  const data = useMemo(() => accountNos?.pages?.reduce((acc, curr) => [...acc, ...curr], []) || [], [accountNos])

  return (
    <ModalContainer alwaysMobile>
      <ModalHeaderContainer classes='!px-0 !pb-0'>
        <div className={MODAL_WRAPPER_X_PADDING}>
          <div className='flex items-center justify-between'>
            <ModalHeader subtitleClasses='!text-[0.8em]' subtitle='Acc #' />

            <div className='relative flex items-center justify-end gap-[5px]'>
              {copyValue && (
                <ButtonCopy
                  textValue={`Copy ${selected.length} selected`}
                  value={copyValue}
                  className='action-brand px-2 !text-[0.85rem] !gap-[3px]'
                  iconClassName='text-[0.9rem] !text-text'
                  stopPropagation={false}
                />
              )}
              {isFiltered && (
                <button
                  type='button'
                  className='action-neutral px-2 !text-[0.85rem] !gap-[3px]'
                  disabled={allLoading || updating}
                  onClick={() => {
                    setMCentreId('')
                    setAccountNo('')
                    setOnlyAvailable(false)
                    setSelected([])
                  }}
                >
                  Clear <commonIcons.close />
                </button>
              )}
              <ModalCloseButton closeModal={() => onDismiss(null, ModalDismiss.Cancel)} />
            </div>
          </div>

          <div className='pt-4 grid sm:grid-cols-2 gap-x-[10px]'>
            <SelectInputSM
              type='text'
              label='Default monitoring centre'
              id='defaultMonitoringCentreId'
              className='capitalize'
              loading={allLoading}
              disabled={loading || updating}
              registration={{
                value: mCentreId || '',
                onChange: (e: any) => {
                  setMCentreId(e.target.value)
                }
              }}
              options={[
                { label: 'Select Monitoring Centre', value: '' },
                ...(monitoringCentres
                  ?.filter((c: ICompanyListModel) => c.companyTypeId === CompanyTypeId.MonitoringCentre)
                  ?.map((c: any) => ({ label: c.companyName, value: c.companyId })) || [])
              ]}
            />
            <ControlledInputDebounced
              type='text'
              placeholder='Account number'
              label='Account number'
              id='unit-management-filter-account-number'
              className={INPUT_CLASSES}
              containerClassName={INPUT_CONTAINER_CLASSES}
              value={accountNo}
              disabled={allLoading || updating}
              onChange={(e) => setAccountNo(e.target.value)}
            />
            <CheckboxInputSM
              label='Show available only'
              id='showAvailableOnly'
              disabled={allLoading || updating}
              containerClassName=''
              registration={{
                checked: onlyAvailable,
                onChange: (e: any) => setOnlyAvailable(e.target.checked)
              }}
            />
          </div>
        </div>
        <div
          className={classNames(MODAL_WRAPPER_X_PADDING, 'z-10 px-[1.5rem] py-[10px] top-0 text-text border border-border backdrop-blur')}
        >
          <div className='px-[10px] grid grid-cols-3'>
            <div className='text-[0.9em]'>Account #</div>
            <div className='text-[0.9em]'>Status</div>
            <div className='flex items-center justify-end text-[0.9em]'>Actions</div>
          </div>
        </div>
      </ModalHeaderContainer>
      <ModalBodyInfiniteScrollable classes='!pt-[280px] sm:!pt-[210px] !px-0'>
        <PullRefresh
          loading={allLoading}
          callback={() => {
            if (!updating) {
              accountNosRefetch()
            }
          }}
        />
        <AnimatePresence mode='wait'>
          <div>
            <div>
              {!loading &&
                data?.map((an, ani) => (
                  <motion.div
                    key={`account-nos-modal-item-${ani}`}
                    {...motionFadeIn(0.2)}
                    className={classNames(
                      MODAL_WRAPPER_X_PADDING,
                      'grid grid-cols-3 py-[15px] text-text',
                      ani % 2 === 0 ? 'bg-surface' : 'bg-surface-2'
                    )}
                  >
                    <div className='flex items-center justify-start jost-medium opacity-60 text-[0.9em]'>{an.accountNo}</div>
                    <div className='flex items-center justify-start'>
                      <AccountNoStatus status={an.status} />
                    </div>
                    <div className='flex items-center justify-end'>
                      {an.status === AccountNoStatusEnum.reserved && (
                        <ModalButton
                          component={ConfirmModal}
                          disabled={updating}
                          classes=''
                          componentProps={{
                            text: 'Remove',
                            description: 'Confirm you would like remove this account number from reserved?',
                            action: ConfirmModalAction.Error
                          }}
                          confirmCallback={() => releaseReserved({ mCentreId: mCentreId || '', accountNo: an.accountNo })}
                          tooltip={{ id: `account-nos-all-modal-remove-reserved-${an.id}`, message: 'Release reservation' }}
                        >
                          <ButtonRoundSm type='div' className='action-erro'>
                            <commonIcons.cancel />
                          </ButtonRoundSm>
                        </ModalButton>
                      )}
                      <ButtonCopy
                        value={an.accountNo}
                        className='w-[30px] h-[30px] action-brand flex items-center justify-center'
                        iconClassName='text-[1rem]'
                      />
                      <div className='ml-[10px] h-[100%] w-[1px] bg-secondary-content/20' />
                      <CheckboxInputSM
                        label=''
                        id={`checkbox-account-no-all-modal-${an.id}`}
                        disabled={updating}
                        containerClassName='!m-0 !p-0 scale-[0.8]'
                        className='!m-0 !p-0'
                        registration={{
                          checked: selected?.some((s) => s.id === an.id) || false,
                          onChange: (e: any) => {
                            const isSelected = selected?.some((s) => s.id === an.id)

                            if (e?.target?.checked && !isSelected) {
                              setSelected((prev) => [...prev, an])
                            } else if (isSelected) {
                              setSelected((prev) => prev.filter((s) => s.id !== an.id))
                            }
                          }
                        }}
                      />
                    </div>
                  </motion.div>
                ))}
              {loading &&
                Array.from(Array(15).keys()).map((k) => (
                  <motion.div
                    key={`account-nos-modal-item-skeleton-${k}`}
                    {...motionFadeIn(0.2)}
                    className='h-[50px] bg-secondary-content/5 animate-pulse mb-[2px]'
                  />
                ))}
              {!loading && mCentreId && mCentreId?.length > 0 && data?.length === 0 && (
                <motion.div
                  key='account-nos-modal-no-results'
                  {...motionFadeIn(0.2)}
                  className={classNames(MODAL_WRAPPER_X_PADDING, 'py-[20px] flex items-center justify-center')}
                >
                  <div className='jost-light flex items-center justify-center h-full bg-surface text-text border border-border px-[30px] py-[20px] rounded-2xl'>
                    No results for monitoring centre.
                  </div>
                </motion.div>
              )}
              {!loading && (!mCentreId || mCentreId?.length === 0) && (
                <motion.div
                  key='account-nos-modal-no-mc-selected'
                  {...motionFadeIn(0.2)}
                  className={classNames(MODAL_WRAPPER_X_PADDING, 'py-[20px] flex items-center justify-center')}
                >
                  <div className='jost flex items-center justify-center h-full bg-surface text-text text-center text-[0.9em] border border-border px-10 py-8 rounded-2xl'>
                    Select a Monitoring Centre to load Account No&apos;s.
                  </div>
                </motion.div>
              )}
            </div>
          </div>
        </AnimatePresence>
        <IonInfiniteScroll
          onIonInfinite={async (ev) => {
            await accountNosFetchNext()
            ev.target.complete()
          }}
        >
          <InfiniteLoader key='account-nos-modal-infinite-loader' loading={accountNosIsFetching} />
        </IonInfiniteScroll>
      </ModalBodyInfiniteScrollable>
    </ModalContainer>
  )
}
